import React, { useState, useEffect } from 'react';
import { Play, X, ExternalLink, Filter } from 'lucide-react';
import './Portfolio.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Featured video - Wedding Demo
const FEATURED_VIDEO = {
  id: 'featured',
  title: 'Wedding Film - Cinematic Highlight',
  description: 'A beautiful cinematic wedding highlight showcasing our signature editing style',
  category: 'Wedding Film',
  client: 'RSE Demo',
  thumbnail: 'https://img.youtube.com/vi/r9g8kqQWWXU/maxresdefault.jpg',
  videoUrl: 'https://www.youtube.com/embed/r9g8kqQWWXU',
  youtubeId: 'r9g8kqQWWXU',
  isFeatured: true
};

export const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const categories = ['all', 'Wedding Film', 'Corporate', 'Social Media', 'Documentary', 'AD Film', 'Event'];

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portfolio`);
      if (response.ok) {
        const data = await response.json();
        // Add featured video at the beginning
        setProjects([FEATURED_VIDEO, ...data.map(item => ({
          ...item,
          thumbnail: item.thumbnail_url,
          videoUrl: item.video_url,
          client: item.client_name || 'Client'
        }))]);
      } else {
        // Fallback to featured video only
        setProjects([FEATURED_VIDEO]);
      }
    } catch (error) {
      console.error('Failed to fetch portfolio:', error);
      setProjects([FEATURED_VIDEO]);
    }
  };

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(project => project.category === filter);

  const openVideoModal = (project) => {
    setSelectedVideo(project);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsModalOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Get YouTube embed URL
  const getEmbedUrl = (project) => {
    if (project.youtubeId) {
      return `https://www.youtube.com/embed/${project.youtubeId}?autoplay=1&rel=0`;
    }
    if (project.videoUrl && project.videoUrl.includes('youtube.com/embed')) {
      return project.videoUrl + '?autoplay=1&rel=0';
    }
    if (project.videoUrl && project.videoUrl.includes('youtu.be')) {
      const id = project.videoUrl.split('/').pop().split('?')[0];
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
    }
    if (project.videoUrl && project.videoUrl.includes('youtube.com/watch')) {
      const urlParams = new URLSearchParams(project.videoUrl.split('?')[1]);
      const id = urlParams.get('v');
      return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0`;
    }
    return null;
  };

  return (
    <section id="portfolio" className="portfolio section">
      <div className="container">
        {/* Filter Tabs */}
        <div className="portfolio-filters">
          <Filter size={18} className="filter-icon" />
          {categories.map((category) => (
            <button
              key={category}
              className={`filter-btn ${filter === category ? 'active' : ''}`}
              onClick={() => setFilter(category)}
              data-testid={`filter-${category}`}
            >
              {category === 'all' ? 'All Projects' : category}
            </button>
          ))}
        </div>

        {/* Featured Video Section */}
        {filter === 'all' && (
          <div className="featured-video-section">
            <div className="featured-badge">Featured Work</div>
            <div 
              className="featured-video-card"
              onClick={() => openVideoModal(FEATURED_VIDEO)}
            >
              <div className="featured-video-thumbnail">
                <img
                  src={FEATURED_VIDEO.thumbnail}
                  alt={FEATURED_VIDEO.title}
                  loading="lazy"
                />
                <div className="featured-play-overlay">
                  <div className="featured-play-button">
                    <Play size={48} fill="currentColor" />
                  </div>
                  <span className="featured-play-text">Watch Video</span>
                </div>
              </div>
              <div className="featured-video-info">
                <span className="featured-category">{FEATURED_VIDEO.category}</span>
                <h3 className="featured-title">{FEATURED_VIDEO.title}</h3>
                <p className="featured-description">{FEATURED_VIDEO.description}</p>
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Grid */}
        <div className="portfolio-grid">
          {filteredProjects.filter(p => !p.isFeatured || filter !== 'all').map((project, index) => (
            <div
              key={project.id}
              className="portfolio-card"
              style={{ animationDelay: `${index * 0.1}s` }}
              onClick={() => openVideoModal(project)}
              data-testid={`portfolio-card-${project.id}`}
            >
              <div className="portfolio-image-wrapper">
                <img
                  src={project.thumbnail}
                  alt={project.title}
                  className="portfolio-image"
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800&q=80';
                  }}
                />
                <div className="portfolio-overlay">
                  <div className="play-button-wrapper">
                    <div className="play-button" aria-label="Play video">
                      <Play size={28} fill="currentColor" />
                    </div>
                    <span className="play-text">Watch</span>
                  </div>
                </div>
                <div className="portfolio-category-badge">{project.category}</div>
              </div>
              <div className="portfolio-content">
                <h3 className="portfolio-title">{project.title}</h3>
                <p className="portfolio-description">{project.description}</p>
                <div className="portfolio-footer">
                  <span className="portfolio-client">Client: {project.client}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="portfolio-empty">
            <p>No projects found in this category</p>
          </div>
        )}
      </div>

      {/* Video Modal */}
      {isModalOpen && selectedVideo && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeVideoModal} aria-label="Close">
              <X size={24} />
            </button>
            <div className="video-modal-content">
              {getEmbedUrl(selectedVideo) ? (
                <iframe
                  src={getEmbedUrl(selectedVideo)}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="video-iframe"
                ></iframe>
              ) : (
                <div className="video-placeholder">
                  <p>Video preview not available</p>
                  {selectedVideo.videoUrl && selectedVideo.videoUrl !== '#' && (
                    <a 
                      href={selectedVideo.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      <ExternalLink size={18} />
                      Watch on YouTube
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="video-modal-info">
              <h3>{selectedVideo.title}</h3>
              <p>{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
